import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router";
import dayjs from "dayjs";

import { get, ref } from "firebase/database";

import { usePlaceNames } from "../hooks/usePlaceNames";

import { database } from "../../firebase/firebase";
import { useNotification } from "../../contexts/NotificationContext";
import { useCatalog } from "../../contexts/CatalogContext";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";

interface Props {
  onAdd?: (data: object) => void;
  onUpdate?: (data: object, key: any) => void;
  body?: any;
}

const currencies = [
  { value: "real", label: "Reais" },
  { value: "dollar", label: "Dólar" },
  { value: "euro", label: "Euro" },
  { value: "pesos", label: "Pesos" },
];

const paymentMethods = [
  { value: "cash", label: "Dinheiro" },
  { value: "pix", label: "Pix" },
  { value: "card", label: "Cartão" },
];

export default function ClientForm({ onAdd, onUpdate, body }: Props) {
  const [data, setData] = useState<any>({
    name: body?.name || "",
    passengersQty: body?.passengersQty || 1,
    vehicle: body?.vehicle || "",
    driver: body?.driver || "",
    flight: body?.flight || "",
    date: body?.date ? dayjs(body?.date).valueOf() : dayjs().valueOf(),
    origin: body?.origin || "",
    destination: body?.destination || "",
    currency: body?.currency || "real",
    paymentMethod: body?.paymentMethod ? body?.paymentMethod.toLowerCase() : "cash",
    observation: body?.observation || "",
    value: body?.value || 0,
    paidValue: body?.paidValue || 0,
    phone: body?.phone || "",
    passenger2: body?.passenger2 || "",
    passenger3: body?.passenger3 || "",
    passenger4: body?.passenger4 || "",
    passenger5: body?.passenger5 || "",
    passenger6: body?.passenger6 || "",
  });
  const [key, setKey] = useState<any>();

  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { showNotification } = useNotification();
  const { drivers, vehicles, suggestions: catalogSuggestions } = useCatalog();
  const placeNames = usePlaceNames();
  const suggestions = useMemo(
    () => Array.from(new Set([...catalogSuggestions, ...placeNames])),
    [catalogSuggestions, placeNames]
  );

  const queryParams = new URLSearchParams(location.search);
  const number = queryParams.get("number");

  useEffect(() => {
    if (number) {
      setData((prev: any) => ({ ...prev, phone: number }));
    }
  }, [number]);

  useEffect(() => {
    if (id) {
      get(ref(database, `booking/${id}`)).then((snapshot) => {
        if (snapshot.exists()) {
          const booking = snapshot.val();
          setKey(id);
          const receivedData: any = {
            name: booking.name || "",
            phone: booking.phone || "",
            vehicle: booking.vehicle || "",
            driver: booking.driver || "",
            passengersQty: booking.passengersQty || 1,
            flight: booking.flight || "",
            date: booking.date ? dayjs(booking.date).valueOf() : dayjs().valueOf(),
            origin: booking.origin || "",
            destination: booking.destination || "",
            value: booking.value || 0,
            paidValue: booking.paidValue || 0,
            currency: booking.currency || "real",
            paymentMethod: booking.paymentMethod || "cash",
            observation: booking.observation || "",
          };
          if (booking.passengersQty >= 2) {
            for (let i = 2; i <= booking.passengersQty; i++) {
              receivedData[`passenger${i}`] = booking[`passenger${i}`] || "";
            }
          }
          setData(receivedData);
        } else {
          navigate("/admin-management");
        }
      });
    }
  }, [id, navigate]);

  const handleChange = (field: string, value: any) => {
    setData((prev: any) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();

    if (!data.name || !data.origin || !data.destination || !data.value || !data.phone || !data.driver) {
      return showNotification(
        "warning",
        "Por favor, preencha todos os campos obrigatórios."
      );
    }

    if (data.passengersQty >= 2) {
      const missing: string[] = [];
      for (let i = 2; i <= data.passengersQty; i++) {
        const keyName = `passenger${i}`;
        if (!data[keyName] || !data[keyName].trim()) {
          missing.push(`passageiro ${i}`);
        }
      }
      if (missing.length) {
        return showNotification(
          "warning",
          `Por favor, preencha o nome dos seguintes passageiros: ${missing.join(", ")}`
        );
      }
    }

    if (key && onUpdate) {
      const payload = { ...data, date: dayjs(data.date).valueOf() };
      return onUpdate(payload, key);
    }

    if (onAdd) {
      return onAdd(data);
    }
  };

  const handleInvertOriginDestination = () => {
    setData((prev: any) => ({
      ...prev,
      origin: prev.destination,
      destination: prev.origin,
    }));
  };

  const passengersArray = Array.from({ length: Math.max(0, data.passengersQty - 1) });

  return (
    <form className="space-y-6" onSubmit={handleSubmit}>
      <section className="space-y-3 rounded-3xl border border-white/10 bg-white/5 p-4">
        <p className="text-xs font-semibold uppercase tracking-[0.4em] text-white/50">
          Reserva
        </p>
        <Input
          value={data.name}
          onChange={(e) => handleChange("name", e.target.value)}
          placeholder="Nome da reserva"
          className="bg-white/5 text-white placeholder:text-white/40"
        />
        {!data.name && <p className="text-xs text-red-400">Campo obrigatório.</p>}
        <Input
          value={data.phone}
          onChange={(e) => handleChange("phone", e.target.value)}
          placeholder="Telefone"
          className="bg-white/5 text-white placeholder:text-white/40"
        />
        {!data.phone && <p className="text-xs text-red-400">Campo obrigatório.</p>}
        <div className="grid gap-3 md:grid-cols-2">
          <div className="space-y-2">
            <label className="text-xs uppercase tracking-[0.3em] text-white/50">
              Veículo*
            </label>
            <Select
              value={data.vehicle}
              onValueChange={(value) => handleChange("vehicle", value)}
            >
              <SelectTrigger className="bg-white/5 text-white">
                <SelectValue placeholder="Selecione" />
              </SelectTrigger>
              <SelectContent>
                {vehicles.map(({ id: vehicleId, data: vehicleData }) => (
                  <SelectItem key={vehicleId} value={vehicleId}>
                    {vehicleData.name} ({vehicleData.plate})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <label className="text-xs uppercase tracking-[0.3em] text-white/50">
              Motorista*
            </label>
            <Select
              value={data.driver}
              onValueChange={(value) => handleChange("driver", value)}
            >
              <SelectTrigger className="bg-white/5 text-white">
                <SelectValue placeholder="Selecione" />
              </SelectTrigger>
              <SelectContent>
                {drivers.map(({ id: driverId, data: driverData }) => (
                  <SelectItem key={driverId} value={driverId}>
                    {driverData.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </section>

      <section className="space-y-3 rounded-3xl border border-white/10 bg-white/5 p-4">
        <p className="text-xs font-semibold uppercase tracking-[0.4em] text-white/50">
          Passageiros
        </p>
        <div className="space-y-2">
          <label className="text-xs uppercase tracking-[0.3em] text-white/50">
            Qtd. Passageiros*
          </label>
          <Select
            value={data.passengersQty.toString()}
            onValueChange={(value) => handleChange("passengersQty", Number(value))}
          >
            <SelectTrigger className="bg-white/5 text-white">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {[1, 2, 3, 4, 5, 6].map((qty) => (
                <SelectItem key={qty} value={qty.toString()}>
                  {qty}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        {passengersArray.map((_, index) => {
          const passengerKey = `passenger${index + 2}`;
          return (
            <div key={passengerKey} className="space-y-1">
              <Input
                value={data[passengerKey]}
                onChange={(e) => handleChange(passengerKey, e.target.value)}
                placeholder={`Passageiro ${index + 2}`}
                className="bg-white/5 text-white placeholder:text-white/40"
              />
              {!data[passengerKey] && (
                <p className="text-xs text-red-400">Campo obrigatório.</p>
              )}
            </div>
          );
        })}
      </section>

      <section className="space-y-3 rounded-3xl border border-white/10 bg-white/5 p-4">
        <p className="text-xs font-semibold uppercase tracking-[0.4em] text-white/50">
          Acompanhamento de voo
        </p>
        <Input
          value={data.flight}
          onChange={(e) => handleChange("flight", e.target.value)}
          placeholder="Número do voo"
          className="bg-white/5 text-white placeholder:text-white/40"
        />
        <div className="space-y-2">
          <label className="text-xs uppercase tracking-[0.3em] text-white/50">
            Data e hora*
          </label>
          <input
            type="datetime-local"
            value={dayjs(data.date).format("YYYY-MM-DDTHH:mm")}
            onChange={(e) => handleChange("date", dayjs(e.target.value).valueOf())}
            className="w-full rounded-2xl border border-white/10 bg-transparent px-3 py-2 text-sm text-white"
          />
        </div>
      </section>

      <section className="space-y-3 rounded-3xl border border-white/10 bg-white/5 p-4">
        <p className="text-xs font-semibold uppercase tracking-[0.4em] text-white/50">
          Origem e destino
        </p>
        <Input
          list="origins"
          value={data.origin}
          onChange={(e) => handleChange("origin", e.target.value)}
          placeholder="Origem"
          className="bg-white/5 text-white placeholder:text-white/40"
        />
        {!data.origin && (
          <p className="text-xs text-red-400">Este campo é obrigatório.</p>
        )}
        <Button type="button" variant="ghost" onClick={handleInvertOriginDestination}>
          Inverter
        </Button>
        <Input
          list="destinations"
          value={data.destination}
          onChange={(e) => handleChange("destination", e.target.value)}
          placeholder="Destino"
          className="bg-white/5 text-white placeholder:text-white/40"
        />
        {!data.destination && (
          <p className="text-xs text-red-400">Este campo é obrigatório.</p>
        )}
        <datalist id="origins">
          {suggestions.map((suggestion, index) => (
            <option key={`origin-${index}`} value={suggestion} />
          ))}
        </datalist>
        <datalist id="destinations">
          {suggestions.map((suggestion, index) => (
            <option key={`destination-${index}`} value={suggestion} />
          ))}
        </datalist>
      </section>

      <section className="space-y-3 rounded-3xl border border-white/10 bg-white/5 p-4">
        <p className="text-xs font-semibold uppercase tracking-[0.4em] text-white/50">
          Pagamento
        </p>
        <Input
          type="number"
          value={data.value}
          onChange={(e) => handleChange("value", Number(e.target.value))}
          placeholder="Valor*"
          className="bg-white/5 text-white placeholder:text-white/40"
        />
        {!data.value && (
          <p className="text-xs text-red-400">Este campo é obrigatório.</p>
        )}
        <Input
          type="number"
          value={data.paidValue}
          onChange={(e) => handleChange("paidValue", Number(e.target.value))}
          placeholder="Valor pago"
          className="bg-white/5 text-white placeholder:text-white/40"
        />
        <Select
          value={data.currency}
          onValueChange={(value) => handleChange("currency", value)}
        >
          <SelectTrigger className="bg-white/5 text-white">
            <SelectValue placeholder="Moeda" />
          </SelectTrigger>
          <SelectContent>
            {currencies.map((currency) => (
              <SelectItem key={currency.value} value={currency.value}>
                {currency.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select
          value={data.paymentMethod}
          onValueChange={(value) => handleChange("paymentMethod", value)}
        >
          <SelectTrigger className="bg-white/5 text-white">
            <SelectValue placeholder="Forma de pagamento" />
          </SelectTrigger>
          <SelectContent>
            {paymentMethods.map((method) => (
              <SelectItem key={method.value} value={method.value}>
                {method.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </section>

      <section className="space-y-3 rounded-3xl border border-white/10 bg-white/5 p-4">
        <p className="text-xs font-semibold uppercase tracking-[0.4em] text-white/50">
          Observações
        </p>
        <Textarea
          value={data.observation}
          onChange={(e) => handleChange("observation", e.target.value)}
          placeholder="Ex. pago metade, levar cadeirinha"
          className="bg-white/5 text-white placeholder:text-white/40"
          rows={3}
        />
      </section>

      <Button type="submit" className="w-full">
        {key ? "Atualizar" : "Adicionar"}
      </Button>
    </form>
  );
}
